import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn } from "../variants";
import {
  ChevronRight,
  ArrowBigDownDash,
  ArrowBigUpDash,
  List,
  Layers,
} from "lucide-react";
import "./Skills.css"; // 导入 CSS 文件

import { getSkills } from "./api"; // 确保路径正确

import { TiChevronRightOutline } from "react-icons/ti";

// 定义动画 variants
const gridVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1, // 每个卡片延迟 0.1s 出现
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    rotateZ: -5,
    scale: 0.95,
  },
  show: {
    opacity: 1,
    y: 0,
    rotateZ: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 14,
    },
  },
};

const Skills = ({ onToggle }) => {
  const [scrollDirection, setScrollDirection] = useState("up");
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [skills, setSkills] = useState([]);
  const containerRef = useRef(null);

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [animatedWidth, setAnimatedWidth] = useState(0);

  // 获取技能数据
  useEffect(() => {
    getSkills()
      .then((data) => {
        setSkills(data.data);
      })
      .catch((err) => console.error("获取技能失败：", err));
  }, []);

  // 创建卡片堆叠效果
  const [isListMode, setIsListMode] = useState(false); // 列表展示状态

  useEffect(() => {
    if (!isListMode && containerRef.current && skills.length > 0) {
      const container = containerRef.current;
      container.innerHTML = "";

      skills.forEach((skill, i) => {
        const card = document.createElement("div");
        card.className = "card";
        card.style.filter = `hue-rotate(${i * 20}deg)`;

        if (i === 0) {
          card.classList.add("first-card");
        }

        // 添加点击事件
        card.addEventListener("click", () => {
          if (i !== 0) {
            // 如果不是第一张卡片
            const cards = container.getElementsByClassName("card");

            // 为前面的卡片添加淡出动画，每个卡片都有延迟
            for (let j = 0; j < i; j++) {
              const frontCard = cards[j];
              frontCard.style.animationDelay = `${j * 0.1}s`;
              frontCard.classList.add("card-fade-out");
            }

            // 一格一格向前移动选中的卡片
            let currentPosition = i;
            const moveStep = () => {
              if (currentPosition > 0) {
                // 移动选中的卡片
                const newZPos = (currentPosition - 1) * -100;
                const newYPos = (currentPosition - 1) * -20;
                const newScale = 1 - (currentPosition - 1) * 0.05;
                card.style.transform = `translateZ(${newZPos}px) translateY(${newYPos}px) scale(${newScale})`;

                // 同步更新后面卡片的位置
                for (let j = i + 1; j < cards.length; j++) {
                  const backCard = cards[j];
                  const backZPos = (j - currentPosition) * -100;
                  const backYPos = (j - currentPosition) * -20;
                  const backScale = 1 - (j - currentPosition) * 0.05;
                  backCard.style.transform = `translateZ(${backZPos}px) translateY(${backYPos}px) scale(${backScale})`;
                }

                currentPosition--;
                setTimeout(moveStep, 100); // 每100ms移动一格
              } else {
                // 移动完成后更新状态
                const newSkills = [...skills];
                // 获取前面的卡片
                const frontCards = newSkills.slice(0, i);
                // 获取选中的卡片
                const selectedSkill = newSkills[i];
                // 移除前面的卡片和选中的卡片
                newSkills.splice(0, i + 1);
                // 将前面的卡片添加到末尾
                newSkills.push(...frontCards);
                // 将选中的卡片放到最前面
                newSkills.unshift(selectedSkill);
                setSkills(newSkills);
              }
            };

            // 开始移动
            setTimeout(moveStep, 200);
          }
        });

        const title = document.createElement("h4");
        title.textContent = skill.title;
        title.className = "card-title";
        card.appendChild(title);

        const content = document.createElement("div");
        content.className = "card-content";

        if (skill.imageUrl) {
          const img = document.createElement("img");
          img.src = skill.imageUrl;
          img.alt = skill.title;
          content.appendChild(img);
        }

        const desc = document.createElement("p");
        desc.textContent = skill.description;
        content.appendChild(desc);

        card.appendChild(content);

        // 设置3D位置和动画
        const zPos = i * -100;
        const yPos = i * -20;
        const xPos = i * -12; // 每层水平错开 16px，12~20 都可以按审美微调
        const scale = 1 - i * 0.05;
        card.style.zIndex = String(1000 - i); // 明确前高后低
        card.style.transform = `translateX(${xPos}px) translateZ(${zPos}px) translateY(${yPos}px) scale(${scale})`;
        card.style.transition = "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)";

        container.appendChild(card);
      });
    }
  }, [skills, isListMode]);

  // 向前移动技能列表
  const rotateSkills = () => {
    if (skills.length > 0) {
      const container = containerRef.current;
      if (container) {
        const cards = container.getElementsByClassName("card");
        if (cards.length > 0) {
          // 添加淡出动画到第一张卡片
          cards[0].classList.add("rotate-out");

          // 为所有其他卡片添加移动动画
          for (let i = 1; i < cards.length; i++) {
            const card = cards[i];
            const newZPos = (i - 1) * -100;
            const newYPos = (i - 1) * -20;
            const newxPos = i * -12;
            const newScale = 1 - (i - 1) * 0.05;

            card.style.transform = `translateX(${newxPos}px) translateZ(${newZPos}px) translateY(${newYPos}px) scale(${newScale})`;
          }

          // 等待动画完成后更新状态
          setTimeout(() => {
            const newSkills = [...skills];
            const firstSkill = newSkills.shift();
            newSkills.push(firstSkill);
            setSkills(newSkills);
          }, 500);
        }
      }
    }
  };

  const reverseRotateSkills = () => {
    if (skills.length > 0) {
      const container = containerRef.current;
      if (container) {
        const cards = container.getElementsByClassName("card");
        if (cards.length > 0) {
          // 添加动画类到最后一张卡片（准备插到最前）
          cards[cards.length - 1].classList.add("rotate-in");

          // 所有其他卡片向后移动一格
          for (let i = 0; i < cards.length - 1; i++) {
            const card = cards[i];
            const newZPos = (i + 1) * -100;
            const newYPos = (i + 1) * -20;
            const newScale = 1 - (i + 1) * 0.05;
            card.style.transform = `translateZ(${newZPos}px) translateY(${newYPos}px) scale(${newScale})`;
          }

          // 延迟后更新状态
          setTimeout(() => {
            const newSkills = [...skills];
            const lastSkill = newSkills.pop();
            newSkills.unshift(lastSkill);
            setSkills(newSkills);
          }, 500);
        }
      }
    }
  };

  // 监听滚动方向
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        setScrollDirection("down");
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection("up");
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (hoveredIndex === null) return;

    // 获取目标进度百分比
    const level = skills[hoveredIndex]?.level ?? "";
    const target =
      {
        Beginner: 25,
        Intermediate: 50,
        Advanced: 75,
        Expert: 100,
      }[level[0]?.toUpperCase() + level.slice(1)?.toLowerCase()] ?? 0;

    let current = 0;
    const interval = setInterval(() => {
      current += 2;
      if (current >= target) {
        current = target;
        clearInterval(interval);
      }
      setAnimatedWidth(current);
    }, 10); // 控制速度

    return () => clearInterval(interval); // 清理旧动画
  }, [hoveredIndex]);

  return (
    <section
      className="min-h-[100vh] lg:min-h-[100vh] flex items-center"
      id="skills"
    >
      {/* Skills Section */}
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-center">
          {/* Skills List */}
          <div
            className=" cursor-pointer relative h-[200px] w-[500px] flex items-center justify-center"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onToggle}
          >
            <AnimatePresence mode="wait">
              {!isHovered ? (
                <motion.div
                  key="skills"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute"
                >
                  <div>
                    <p className="font-bold text-6xl lg:text-9xl text-center">
                      Skills
                    </p>
                    <p className="text-xl lg:text-3xl font-semibold pl-2 flex items-center gap-2">
                      Click to explore my experience
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                        className="inline-block"
                      >
                        <TiChevronRightOutline size={28} />
                      </motion.span>
                    </p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="experience"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute flex items-center gap-4"
                >
                  <div>
                    <p className="font-bold text-6xl lg:text-9xl text-rose-600 dark:text-green-500">
                      Experience
                    </p>
                    <p className="text-xl lg:text-3xl pl-2 text-rose-600 dark:text-green-500">
                      View my journey
                    </p>
                  </div>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ChevronRight className="w-20 h-20 text-rose-600 dark:text-green-500" />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => setIsListMode(!isListMode)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 items-center gap-2 hidden lg:flex "
          >
            {isListMode ? (
              <>
                <Layers size={18} /> Switch to 3D Mode
              </>
            ) : (
              <>
                <List size={18} /> Switch to List Mode
              </>
            )}
          </button>

          {/* 小屏幕下的普通卡片列表 */}
          <ul className="lg:hidden grid grid-cols-2 gap-6 w-full">
            {skills.map((skill, index) => (
              <motion.li
                key={index}
                variants={fadeIn("up", 0.3)}
                initial="hidden"
                whileInView={"show"}
                viewport={{ once: false, amount: 0.7 }}
                className="flex flex-col items-center text-center p-4 bg-white/10 rounded-xl shadow-md"
              >
                {skill.imageUrl && (
                  <img
                    src={skill.imageUrl}
                    alt={skill.title}
                    className="w-16 h-16 mx-auto mb-2"
                  />
                )}
                <h4 className="text-xl font-bold">{skill.title}</h4>
                <p className="text-sm">{skill.desc}</p>
              </motion.li>
            ))}
          </ul>

          {/* 大屏幕下的3D堆叠效果 */}

          <div className="w-full max-w-7xl mx-auto hidden lg:block mt-8">
            {isListMode ? (
              // ✅ 列表展示模式
              <div className="grid grid-cols-4 gap-6 w-full">
                {skills.map((skill, index) => (
                  <motion.div
                    key={index}
                    variants={fadeIn("up", 0.3)}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: false, amount: 0.7 }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => {
                      setHoveredIndex(null);
                      setAnimatedWidth(0);
                    }}
                    className="group relative flex flex-col items-center text-center p-4 bg-white/10 rounded-xl shadow-md overflow-hidden  hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(59,130,246,0.4)]"
                  >
                    {/* ✅ 背景遮罩，hover 出现 */}
                    <div className="absolute inset-0 bg-black/0 group-hover:backdrop-blur-xl group-hover:bg-black/60 transition-all duration-300 z-10 rounded-xl" />

                    {/* ✅ 图片容器，初始居中，hover 往左平移 */}
                    <div className="relative z-20 flex justify-center w-full">
                      <img
                        src={skill.imageUrl}
                        alt={skill.title}
                        className="
          w-16 h-16 mb-2
          transition-all duration-500
          transform
          group-hover:translate-x-[-100px]
        "
                      />
                    </div>
                    <div className="absolute z-20 top-4 p-2 ">
                      {/* ✅ hover 时显示等级替代标题 */}
                      <h4 className="text-xl font-bold absolute left-0 top-0 transition-opacity duration-300 opacity-0 group-hover:opacity-100 text-white">
                        Level: {skill.level}
                      </h4>
                    </div>

                    <div className="relative z-20 w-full">
                      {" "}
                      {/* 设置一个固定的 min-h */}
                      {/* ✅ 默认显示标题（hover 时隐藏） */}
                      <h4 className="text-xl font-bold transition-opacity duration-300 group-hover:opacity-0">
                        {skill.title}
                      </h4>
                      {/* ✅ hover 时显示进度条替代标题 */}
                      <div className="absolute left-0 top-0 w-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden mt-1">
                          <div
                            className="
          h-full
          transition-all duration-500
          rounded-full
          bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500
          shadow-[0_0_10px_rgba(0,0,0,0.3)]
        "
                            style={{
                              width:
                                hoveredIndex === index
                                  ? `${animatedWidth}%`
                                  : "0%",
                            }}
                          ></div>
                        </div>
                      </div>
                      {/* ✅ 描述始终显示 */}
                      <p className="text-sm text-black/80 dark:text-white/80">
                        {skill.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              // ✅ 3D 卡片堆叠模式
              <div className="relative flex flex-row items-center justify-between">
                {/* 左侧技能详情 */}
                <AnimatePresence mode="wait">
                  {skills.length > 0 && (
                    <motion.div
                      key={skills[0].title} // ⭐关键：每次更换卡片时触发重渲染动画
                      variants={fadeIn("left", 0.3)}
                      initial={{ opacity: 0, x: 0 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-[5%] w-[40%] card-details rounded-lg flex flex-col items-center justify-center text-center"
                    >
                      <h3 className="text-2xl font-bold mb-4">
                        {skills[0].title}
                      </h3>

                      {skills[0].imageUrl && (
                        <img
                          src={skills[0].imageUrl}
                          alt={skills[0].title}
                          className="w-16 h-16 mb-4"
                        />
                      )}

                      <p className="dark:text-white/90 text-black/90 text-lg leading-relaxed">
                        {skills[0].description}
                      </p>

                      {/* 技能等级进度条 */}
                      <div className="w-full mt-6">
                        <div className="relative h-3 w-full rounded-full bg-gray-700 overflow-hidden">
                          <div
                            className="
              h-full animate-wave
              bg-[length:300%_100%] bg-repeat-x rounded-full
              transition-all duration-500
              shadow-[0_0_12px_rgba(255,255,255,0.3)]
              dark:bg-[linear-gradient(90deg,_#8b5cf6,_#3b82f6,_#06b6d4)]
              bg-[linear-gradient(90deg,_#ef4444,_#f97316,_#facc15)]
            "
                            style={{
                              width: `${
                                {
                                  Beginner: 5,
                                  Intermediate: 40,
                                  Advanced: 70,
                                  Expert: 100,
                                }[
                                  skills[0].level?.[0]?.toUpperCase() +
                                    skills[0].level?.slice(1)?.toLowerCase()
                                ] ?? 0
                              }%`,
                            }}
                          ></div>
                        </div>

                        <div className="flex justify-between mt-2 text-xs dark:text-white/70 text-black/70">
                          {[
                            "Beginner",
                            "Intermediate",
                            "Advanced",
                            "Expert",
                          ].map((level) => {
                            const currentLevel =
                              skills[0].level?.[0]?.toUpperCase() +
                              skills[0].level?.slice(1)?.toLowerCase();
                            const isActive = currentLevel === level;

                            return (
                              <span
                                key={level}
                                className={`min-w-[60px] text-center transition-all duration-300 ${
                                  isActive
                                    ? "text-base font-bold scale-110"
                                    : ""
                                }`}
                              >
                                {level}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* 中间卡片区域 */}
                <motion.div
                  variants={fadeIn("right", 0.3)}
                  initial="hidden"
                  whileInView={"show"}
                  viewport={{ once: false, amount: 0.3 }}
                >
                  <div
                    className="skills-container mx-auto "
                    ref={containerRef}
                  ></div>
                </motion.div>

                {/* 右侧切换按钮 */}
                <div className="flex flex-col mt-[5%]">
                  <button
                    onClick={reverseRotateSkills}
                    className="relative transition-all duration-300 hover:scale-150 mb-10"
                  >
                    <ArrowBigUpDash className="w-10 h-10" />
                  </button>
                  <button
                    onClick={rotateSkills}
                    className="relative transition-all duration-300 hover:scale-150 mt-10"
                  >
                    <ArrowBigDownDash className="w-10 h-10" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
